import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

type FetchInput = RequestInfo | URL;

@Injectable({ providedIn: 'root' })
export class CsrfService {
  private readonly csrfCookieName = 'XSRF-TOKEN';
  private readonly csrfHeaderName = 'X-CSRF-TOKEN';
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly apiOrigin = new URL(this.apiBaseUrl).origin;
  private tokenRequest?: Promise<string>;
  private accessTokenRequest?: Promise<string>;
  private originalFetch?: typeof window.fetch;

  constructor(private msalService: MsalService) {}

  installFetchInterceptor(): void {
    if (typeof window === 'undefined' || this.originalFetch) {
      return;
    }

    this.originalFetch = window.fetch.bind(window);

    window.fetch = async (input: FetchInput, init?: RequestInit): Promise<Response> => {
      const requestUrl = this.resolveUrl(input);
      const method = this.resolveMethod(input, init);

      if (!this.shouldAuthenticate(requestUrl)) {
        return this.originalFetch!(input, init);
      }

      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      const accessToken = await this.getAccessToken();

      headers.set('Authorization', `Bearer ${accessToken}`);

      if (this.shouldProtect(method)) {
        const token = await this.getToken();
        headers.set(this.csrfHeaderName, token);
      }

      return this.originalFetch!(input, {
        ...init,
        headers,
        credentials: this.shouldProtect(method) ? 'include' : init?.credentials,
      });
    };
  }

  async getToken(): Promise<string> {
    const cookieToken = this.readCookie(this.csrfCookieName);
    if (cookieToken) {
      return cookieToken;
    }

    if (!this.tokenRequest) {
      this.tokenRequest = this.fetchToken().finally(() => {
        this.tokenRequest = undefined;
      });
    }

    return this.tokenRequest;
  }

  private async getAccessToken(): Promise<string> {
    if (!this.accessTokenRequest) {
      this.accessTokenRequest = this.fetchAccessToken().finally(() => {
        this.accessTokenRequest = undefined;
      });
    }

    return this.accessTokenRequest;
  }

  private async fetchAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount()
      || this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      throw new Error('Unable to authorize backend request: no active MSAL account');
    }

    const result = await firstValueFrom(this.msalService.acquireTokenSilent({
      scopes: environment.apiConfig.scopes,
      account,
    }));

    if (!result.accessToken) {
      throw new Error('Unable to authorize backend request: MSAL did not return an access token');
    }

    return result.accessToken;
  }

  private async fetchToken(): Promise<string> {
    const fetchImpl = this.originalFetch || window.fetch.bind(window);
    const response = await fetchImpl(`${this.apiBaseUrl}/csrf-token`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to initialize CSRF token: HTTP ${response.status}`);
    }

    const body = await response.json();
    const token = body?.csrfToken || this.readCookie(this.csrfCookieName);
    if (!token) {
      throw new Error('Unable to initialize CSRF token: response did not include a token');
    }

    return token;
  }

  private shouldAuthenticate(url: URL): boolean {
    return url.origin === this.apiOrigin && url.pathname !== '/csrf-token';
  }

  private shouldProtect(method: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
  }

  private resolveUrl(input: FetchInput): URL {
    const url = input instanceof Request ? input.url : input.toString();
    return new URL(url, window.location.origin);
  }

  private resolveMethod(input: FetchInput, init?: RequestInit): string {
    return init?.method || (input instanceof Request ? input.method : 'GET');
  }

  private readCookie(name: string): string | null {
    const prefix = `${encodeURIComponent(name)}=`;
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    return match ? decodeURIComponent(match.substring(prefix.length)) : null;
  }
}
